// TODO: migrate from legacy CI tooling: resolve ts-* and/or eslint-* directives below (including inline comments)
/* eslint-disable @typescript-eslint/await-thenable, @typescript-eslint/no-floating-promises, @typescript-eslint/no-misused-promises, @typescript-eslint/only-throw-error, react-hooks/exhaustive-deps, react-hooks/use-memo */

import _ from "lodash";
import PropTypes from "prop-types";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { Button, Divider, Form, Icon, Message } from "semantic-ui-react";
import * as companyActions from "../../../../redux/actions/companyActions";
import * as farmActions from "../../../../redux/actions/farmActions.ts";
import * as lookupActions from "../../../../redux/actions/lookupActions";
import * as mapboxSearchActions from "../../../../redux/actions/mapboxSearchActions";
import CooperatorModal from "./CooperatorModal.tsx";
import "./form.css";
import validate from "./formValidation";

const NewEditFarm = ({
	clientId,
	getTokenSilently,
	userId,
	setSaveFunction,
	setIsDirty,
	filledForm,
	setFilledFormMember,
	mode = "new",
	onClearForm,
}) => {
	const dispatch = useDispatch();

	const [openCooperatorModal, setOpenCooperatorModal] = useState(false);
	const [countryOptions, setCountryOptions] = useState([]);
	const [allRegionData, setAllRegionData] = useState([]);
	const [regionOptions, setRegionOptions] = useState([]);
	const [addressOptions, setAddressOptions] = useState([]);
	const [countryDropdownLoading, setCountryDropdownLoading] = useState(true);
	const [regionDropdownLoading, setRegionDropdownLoading] = useState(true);
	const [cooperatorDropdownLoading, setCooperatorDropdownLoading] = useState(true);
	const [regionOptionsDisabled, setRegionOptionsDisabled] = useState(true);
	const [cooperatorCompanyOptions, setCooperatorCompanyOptions] = useState([]);
	const [valid, setValid] = useState(true);
	const [messages, setMessages] = useState({});
	const [companySaving, setCompanySaving] = useState(false);
	const [companyCodeGenerating, setCompanyCodeGenerating] = useState(false);
	const [newCompany, setNewCompany] = useState({});
	const [clearConfirmed, setClearConfirmed] = useState(false);

	// const filledForm = useSelector((state) => state.farmForm ?? null);
	const setFilledForm = (form, silent = false) => {
		setFilledFormMember(form);
		if (!silent) {
			setIsDirty(true);
		}
	};
	const selectedRegionId = useMemo(() => filledForm.regionId, [regionOptions, filledForm.regionId]);
	const selectedCountryId = useMemo(() => filledForm.countryId, [countryOptions, filledForm.countryId]);
	const selectedCooperatorCompanyId = useMemo(
		() => filledForm.cooperatorCompanyId,
		[cooperatorCompanyOptions, filledForm.cooperatorCompanyId],
	);

	useEffect(() => {
		getDropDowns();
		getCooperatorCompanyType();
		setFilledForm({ clientId: clientId }, true);
	}, []);

	useEffect(() => {
		if (allRegionData.length > 0 && selectedCountryId) {
			setupRegionOptions(allRegionData, selectedCountryId);
		}
	}, [allRegionData, selectedCountryId]);

	// useEffect(() => {
	// 	if (mode === "edit" && farmId) {
	// 		getFarmInfo();
	// 	}
	// }, [farmId]);

	useEffect(() => {
		debouncedValidate(filledForm);
	}, [filledForm]);

	useEffect(() => {
		setNewCompany((prev) => ({ ...prev, clientId: clientId, userId: userId }));
	}, [userId, clientId]);

	useEffect(() => {
		if (newCompany.id) {
			setFilledForm({ cooperatorCompanyId: newCompany.id });
		}
	}, [newCompany, cooperatorCompanyOptions]);

	useEffect(() => {
		setSaveFunction(async () => {
			const validation = validate(filledForm, true);
			setValid(!validation.failed);
			setMessages(validation.messages);

			if (!validation.failed) {
				if (mode === "new") {
					return await createFarm();
				} else if (mode === "edit") {
					await updateFarm();
				}
			} else {
				throw "Farm information did not pass validation. Please fix any errors and try again.";
			}
		});
	}, [filledForm]);

	useEffect(() => {
		if (clearConfirmed) {
			const id = setTimeout(unconfirmClear, 4000);
			return () => {
				clearTimeout(id);
			};
		}
	}, [clearConfirmed]);

	const getCooperatorCompanyType = async () => {
		const accessToken = await getTokenSilently();
		const res = await dispatch(companyActions.getCompanyTypes(clientId, accessToken));
		const cooperatorCompanyTypeId = res.data.find((t) => t.name === "Cooperator").id;
		setNewCompany((prev) => ({ ...prev, companyTypeIds: [cooperatorCompanyTypeId] }));
	};

	const getDropDowns = async () => {
		await getCountries();
		await getRegions();
		await getCooperatorCompanies();
	};

	const getCountries = async () => {
		const accessToken = await getTokenSilently();
		dispatch(lookupActions.getCountries(clientId, accessToken))
			.then((res) => {
				if (res.statusCode === 200) {
					setupCountryOptions(res.data);
				} else {
					toast.error("Unable to load countries. Please try again.");
				}
			})
			.catch(() => {
				toast.error("Unable to load countries. Please try again.");
			});
	};

	const getRegions = async () => {
		const accessToken = await getTokenSilently();
		dispatch(lookupActions.getRegions(clientId, accessToken))
			.then((res) => {
				if (res.statusCode === 200) {
					setAllRegionData(res.data);
					setRegionDropdownLoading(false);
				} else {
					toast.error("Unable to load regions. Please try again.");
				}
			})
			.catch(() => {
				toast.error("Unable to load regions. Please try again.");
			});
	};

	const getCooperatorCompanies = async () => {
		const accessToken = await getTokenSilently();
		dispatch(companyActions.getCooperatorCompanies(clientId, accessToken))
			.then((res) => {
				if (res.data.length > 0) {
					setupCooperatorCompanyOptions(res.data);
				}
			})
			.catch((err) => {
				console.error(err);
			});
	};

	async function saveCooperatorCompany() {
		setCompanySaving(true);
		const accessToken = await getTokenSilently();
		dispatch(companyActions.createCompany(newCompany, accessToken))
			.then((res) => {
				if (res.statusCode === 200) {
					toast.success("Created!");
					setCooperatorDropdownLoading(true);
					getCooperatorCompanies();
					setNewCompany((prev) => ({ ...prev, id: res.data }));
					setOpenCooperatorModal(false);
				} else if (res.statusCode === 400) {
					if (res.messages && res.messages.length > 0) {
						res.messages.forEach((m) => {
							if (m.type === "Informational") {
								toast.info(m.text);
							} else if (m.type === "FailedValidation") {
								toast.error(m.text);
							}
						});
					}
				}
				setCompanySaving(false);
			})
			.catch((err) => {
				toast.error("Unable to create new company. Please try again.");
				setCompanySaving(false);
				console.error(err);
			});
	}

	async function generateCompanyCode() {
		setCompanyCodeGenerating(true);
		const accessToken = await getTokenSilently();
		if (newCompany.name) {
			dispatch(companyActions.generateCompanyCode(newCompany.clientId, newCompany.name, accessToken))
				.then((res) => {
					setNewCompany((prev) => ({ ...prev, companyCode: res.data }));
					setCompanyCodeGenerating(false);
				})
				.catch((err) => {
					console.error(err);
					toast.error("Failed to generate company code.");
					setCompanyCodeGenerating(false);
				});
		} else {
			toast.info("Please enter a company name to generate a company code.");
			setCompanyCodeGenerating(false);
		}
	}

	const setupCountryOptions = (cOptions) => {
		const countryOpts = _.map(cOptions, ({ id, name }) => {
			const opt = { key: id, value: id, text: name };
			return opt;
		});
		setCountryOptions(countryOpts);
		setCountryDropdownLoading(false);
	};

	const setupRegionOptions = (sOptions, cId) => {
		const regionOpts = _.reduce(
			sOptions,
			(acc, { id, name, countryId }) => {
				if (countryId === cId) {
					const opt = { key: id, value: id, text: name };
					acc.push(opt);
				}
				return acc;
			},
			[],
		);
		setRegionOptions(regionOpts);
		setRegionOptionsDisabled(false);
	};

	function setupCooperatorCompanyOptions(data) {
		const opts = _.map(data, ({ id, name }) => {
			const opt = { key: id, value: id, text: name };
			return opt;
		});
		setCooperatorCompanyOptions(opts);
		setCooperatorDropdownLoading(false);
	}

	const queryAddress = useCallback(async (query) => {
		const res = await dispatch(mapboxSearchActions.getSearchSuggestions(query, userId, { types: "address,poi" }));
		const suggestionOptions = _.map(res.suggestions, (s) => ({
			key: s.mapbox_id,
			value: s,
			text: s.full_address,
		}));

		setAddressOptions(suggestionOptions);
	}, []);

	const retrieveAddress = useCallback(async (suggestion) => {
		const res = await dispatch(mapboxSearchActions.retrieveFromSearchSuggestion(suggestion.mapbox_id, userId));
		return res.features[0];
	}, []);

	const debouncedQueryAddress = useCallback(_.debounce(queryAddress, 500), []);

	const reverseGeocode = useCallback(async (long, lat) => {
		const res = await dispatch(mapboxSearchActions.getReverseGeocode(long, lat, { types: "address" }));
		return res.features[0].properties;
	}, []);

	const handleSelection = async (data, fromCoords = false) => {
		if (!data) {
			setFilledForm({
				address1: "",
			});
			return;
		}
		const suggestion = data;
		const country = _.find(countryOptions, (c) => c.text === suggestion.context.country?.name);
		const region = _.find(allRegionData, (s) => s.name === suggestion.context.region?.name);
		const feature = fromCoords ? null : await retrieveAddress(suggestion);

		if (fromCoords) {
			setFilledForm({
				address1: suggestion.context.address?.name,
				city: suggestion.context.place?.name,
				countryId: country?.value,
				regionId: region?.id,
				postalCode: suggestion.context.postcode?.name,
			});
		} else {
			setFilledForm({
				address1: suggestion.context.address?.name,
				city: suggestion.context.place?.name,
				countryId: country?.value,
				regionId: region?.id,
				postalCode: suggestion.context.postcode?.name,
				lat: feature.geometry?.coordinates[1],
				long: feature.geometry?.coordinates[0],
			});
		}
	};

	const debouncedValidate = useCallback(
		_.debounce((form) => {
			const validation = validate(form, false);
			setValid(!validation.failed);
			setMessages(validation.messages);
			return validation;
		}, 500),
		[],
	);

	const createFarm = useCallback(async () => {
		const accessToken = await getTokenSilently();
		try {
			const res = await dispatch(farmActions.createFarm(accessToken, filledForm));
			const newId = res.data;
			setIsDirty(false);
			return newId;
		} catch (err) {
			console.error(err);
			toast.error("Unable to create farm.");
		}
	}, [filledForm]);

	const updateFarm = useCallback(async () => {
		const accessToken = await getTokenSilently();
		try {
			await dispatch(farmActions.updateFarm(accessToken, filledForm));
			setIsDirty(false);
		} catch (err) {
			console.error(err);
			toast.error("Unable to update farm.");
		}
	}, [filledForm]);

	const unconfirmClear = useCallback(() => {
		setClearConfirmed(false);
	}, []);

	const handleClearForm = useCallback(
		(e) => {
			e.stopPropagation();
			if (clearConfirmed) {
				onClearForm();
				setClearConfirmed(false);
			} else {
				setClearConfirmed(true);
			}
		},
		[clearConfirmed],
	);

	return (
		<>
			<Form style={{ width: "100%", maxWidth: 1000 }} error={!valid}>
				<Form.Group widths={"equal"}>
					<Form.Input
						label="Farm Name"
						id="form-input-name"
						required
						error={messages?.name ? { content: messages.name, pointing: "below" } : false}
						value={filledForm.name}
						onChange={(_e, data) => {
							setFilledForm({ name: data.value });
						}}
					/>
					<Form.Input
						label="Abbreviation"
						id="form-input-abbr"
						required
						error={messages?.abbreviation ? { content: messages.abbreviation, pointing: "below" } : false}
						value={filledForm.abbreviation}
						onChange={(_e, data) => {
							setFilledForm({ abbreviation: data.value });
						}}
					/>
				</Form.Group>
				<Form.Group widths={"equal"}>
					<Form.Dropdown
						label={
							<div className="buttonLabelGroup">
								<label>Address 1</label>
								<Button
									primary
									disabled={!filledForm.lat || !filledForm.long}
									content="Fill from Co-ords"
									style={{ padding: "1px 6px", borderRadius: "6px", fontSize: "12px" }}
									onClick={async (e) => {
										e.preventDefault();
										const data = await reverseGeocode(filledForm.long, filledForm.lat);
										handleSelection(data, true);
									}}
								/>
							</div>
						}
						id="form-input-adr1"
						onSearchChange={(_e, { searchQuery }) => {
							debouncedQueryAddress(searchQuery);
						}}
						search={(opts) => opts}
						value={filledForm.address1}
						options={addressOptions}
						selectOnBlur={false}
						onChange={(_e, { value }) => {
							handleSelection(value);
						}}
						text={filledForm.address1}
						selection
						clearable
					/>
					<Form.Input
						label="Address 2"
						id="form-input-adr2"
						error={messages?.address2 ? { content: messages.address2, pointing: "below" } : false}
						value={filledForm.address2}
						onChange={(_e, data) => {
							setFilledForm({ address2: data.value });
						}}
					/>
				</Form.Group>
				<Form.Group widths={"equal"}>
					<Form.Select
						required
						label="Country"
						id="form-input-country"
						search
						// error={messages?.stateId ? { content: messages.stateId, pointing: "below" } : false}
						value={selectedCountryId}
						options={countryOptions}
						loading={countryDropdownLoading}
						onChange={(_e, data) => {
							setFilledForm({ countryId: data.value });
						}}
					/>
					<Form.Input
						required
						label="City"
						id="form-input-city"
						error={messages?.city ? { content: messages.city, pointing: "below" } : false}
						value={filledForm.city}
						onChange={(_e, data) => {
							setFilledForm({ city: data.value });
						}}
					/>
					<Form.Select
						required
						label="State / Province / Region"
						id="form-input-region"
						search
						error={messages?.regionId ? { content: messages.regionId, pointing: "below" } : false}
						value={selectedRegionId}
						options={regionOptions}
						loading={regionDropdownLoading}
						disabled={regionOptionsDisabled}
						onChange={(_e, data) => {
							setFilledForm({ regionId: data.value });
						}}
					/>
					<Form.Input
						label="Postal Code"
						id="form-input-postal"
						error={messages?.postalCode ? { content: messages.postalCode, pointing: "below" } : false}
						value={filledForm.postalCode}
						onChange={(_e, data) => {
							setFilledForm({ postalCode: data.value });
						}}
					/>
				</Form.Group>
				<Form.Group widths={16}>
					<Form.Input
						label="Latitude"
						id="form-input-lat"
						width={4}
						type="number"
						error={messages?.lat ? { content: messages.lat, pointing: "below" } : false}
						value={filledForm.lat}
						onChange={(_e, data) => {
							setFilledForm({ lat: data.value });
						}}
					/>
					<Form.Input
						label="Longitude"
						id="form-input-long"
						width={4}
						type="number"
						error={messages?.long ? { content: messages.long, pointing: "below" } : false}
						value={filledForm.long}
						onChange={(_e, data) => {
							setFilledForm({ long: data.value });
						}}
					/>
					<Form.Select
						search
						label={
							<div className="buttonLabelGroup">
								<label>Cooperator Company</label>
								<Button
									primary
									content="+ Add New"
									style={{ padding: "1px 6px", borderRadius: "6px", fontSize: "12px" }}
									onClick={(e) => {
										e.preventDefault();
										setOpenCooperatorModal(true);
									}}
								/>
							</div>
						}
						required
						width={8}
						value={selectedCooperatorCompanyId}
						options={cooperatorCompanyOptions}
						loading={cooperatorDropdownLoading}
						error={messages?.cooperatorCompanyId ? { content: messages.cooperatorCompanyId, pointing: "below" } : false}
						onChange={(_e, data) => {
							setFilledForm({ cooperatorCompanyId: data.value });
						}}
					/>
				</Form.Group>
				<Form.Group widths={16} style={{ alignItems: "flex-end" }}>
					<Form.Input
						width={8}
						label="Land Owner"
						id="form-input-owner"
						error={messages?.owner ? { content: messages.owner, pointing: "below" } : false}
						value={filledForm.owner}
						onChange={(_e, data) => {
							setFilledForm({ owner: data.value });
						}}
					/>
					<div style={{ flexGrow: 1 }} />
					<Form.Button
						className={clearConfirmed ? "confirmAction" : ""}
						style={{ marginRight: "6px" }}
						negative
						onClick={handleClearForm}
					>
						{clearConfirmed ? "Confirm?" : "Clear Form"}
					</Form.Button>
				</Form.Group>
				<Form.Checkbox
					label="Is Active?"
					id="form-checkbox-active"
					checked={filledForm.isActive}
					onChange={(_e, data) => {
						setFilledForm({ isActive: data.checked });
					}}
				/>
				<Divider />
				<Form.Group widths={16}>
					<Form.TextArea
						label="Notes"
						width={16}
						id="form-textarea-notes"
						error={messages?.notes ? { content: messages.notes, pointing: "below" } : false}
						value={filledForm.notes}
						onChange={(_e, data) => {
							setFilledForm({ notes: data.value });
						}}
					/>
				</Form.Group>
			</Form>
			<Message style={{ borderRadius: 8 }}>
				<Message.Content>
					Submit a ticket using the <Icon style={{ margin: 0 }} className="info circle" size="small" /> button on the
					left if desired country/region is missing
				</Message.Content>
			</Message>
			<CooperatorModal
				open={openCooperatorModal}
				setOpen={(isOpen) => setOpenCooperatorModal(isOpen)}
				generating={companyCodeGenerating}
				saving={companySaving}
				onGenerateCompanyCode={generateCompanyCode}
				onSaveCompany={saveCooperatorCompany}
				newCompany={newCompany}
				setNewCompany={setNewCompany}
			/>
		</>
	);
};

NewEditFarm.propTypes = {
	farmId: PropTypes.string,
	setSaveFunction: PropTypes.func.isRequired,
	clientId: PropTypes.string.isRequired,
	userId: PropTypes.string.isRequired,
	getTokenSilently: PropTypes.func.isRequired,
	setIsDirty: PropTypes.func,
	mode: PropTypes.string,
	filledForm: PropTypes.object,
	setFilledFormMember: PropTypes.func,
	onClearForm: PropTypes.func,
};

export default NewEditFarm;
